import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, AlertCircle, AlertTriangle, Info, X } from 'lucide-react';

// Type → Lucide icon. Colors come from the per-type CSS rules, not here.
const TYPE_ICONS = {
  success: CheckCircle,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

/**
 * Top-anchored notification stack — layered just below the `.topbar` as an
 * overlay, so it never covers the title nor pushes content down.
 *
 * Each message is a flat paper card: a type-colored Lucide icon, wrapping
 * ink text (never single-line ellipsis), and a × close button. Framer-motion
 * drives the 160ms ease-out enter/exit plus the stack reflow via `layout`.
 *
 * @param {{id:number,type:string,message:string}[]} messages
 * @param {(id:number)=>void} onDismiss
 */
function MessageBar({ messages, onDismiss }) {
  return (
    <div className="message-bar" aria-live="polite" aria-relevant="additions removals">
      <AnimatePresence initial={false}>
        {messages.map(msg => {
          const Icon = TYPE_ICONS[msg.type] || Info;
          return (
            <motion.div
              key={msg.id}
              className={`message-item message-item-${msg.type}`}
              role={msg.type === 'error' ? 'alert' : 'status'}
              initial={{ y: -8, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -6, opacity: 0 }}
              transition={{ duration: 0.16, ease: 'easeOut' }}
              layout
            >
              <Icon className="message-item-icon" size={16} strokeWidth={2} aria-hidden="true" />
              <span className="message-item-text">{msg.message}</span>
              <button
                type="button"
                className="message-item-close"
                aria-label="Dismiss"
                title="Dismiss"
                onClick={() => onDismiss(msg.id)}
              >
                <X size={12} strokeWidth={2} aria-hidden="true" />
              </button>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}

export default MessageBar;
